import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { Card, Btn, Field, Input, Select, Th, useMasters } from "./camCommon";

const KINDS = [
  { key: "commodity", label: "Commodities", attrs: [
    { k: "group", label: "Group", type: "select", options: ["Crude", "Refined", "Biofuel", "NGL", "Other"] },
    { k: "uom", label: "UoM", type: "select", options: ["bbl", "gal", "MT", "m3"] },
    { k: "market_price", label: "Market Price", type: "number" }] },
  { key: "location", label: "Locations", attrs: [
    { k: "type", label: "Type", type: "select", options: ["terminal", "tank_farm", "rack", "refinery", "port"] },
    { k: "region", label: "Region", type: "text" },
    { k: "capacity", label: "Capacity", type: "number" }] },
  { key: "counterparty", label: "Counterparties", attrs: [
    { k: "type", label: "Type", type: "select", options: ["trader", "refiner", "producer", "carrier", "end_user"] },
    { k: "credit_limit", label: "Credit Limit", type: "number" }] },
  { key: "transport_mode", label: "Transport Modes", attrs: [] },
];

export default function CamMasters() {
  const { byKind, refresh } = useMasters();
  const [tab, setTab] = useState("commodity");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const cfg = KINDS.find((k) => k.key === tab);
  const items = byKind(tab);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setAttr = (k) => (e) => setForm((f) => ({ ...f, attrs: { ...f.attrs, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const attrs = { ...form.attrs };
      cfg.attrs.forEach((a) => {
        if (a.type === "number" && attrs[a.k] !== "" && attrs[a.k] !== undefined) attrs[a.k] = Number(attrs[a.k]);
      });
      await api.post("/cam/masters", { ...form, kind: tab, attrs });
      toast.success(form.id ? "Updated" : "Created");
      setForm(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.code} — ${m.name}?`)) return;
    try {
      await api.delete(`/cam/masters/${m.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="cam-masters">
      <div className="flex gap-1 border-b border-slate-200" data-testid="masters-tabs">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => { setTab(k.key); setForm(null); }}
            data-testid={`masters-tab-${k.key}`}
            className={`px-4 py-2 text-[12px] font-semibold tracking-wide border-b-2 -mb-px transition-colors ${
              tab === k.key ? "border-[#C8A24B] text-[#0B1E3F]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {k.label}
          </button>
        ))}
      </div>

      <Card title={cfg.label} subtitle="Reference data every deal, movement and stock position validates against"
        actions={
          <Btn onClick={() => setForm({ code: "", name: "", attrs: {} })} data-testid="master-new-btn">
            <Plus size={13} className="inline mr-1" /> Add
          </Btn>
        } testid={`masters-card-${tab}`}>

        {form && (
          <div className="mb-5 border border-[#C8A24B]/40 bg-amber-50/30 rounded-sm p-4" data-testid="master-form">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0B1E3F]">
                {form.id ? `Edit ${form.code}` : `New ${cfg.label.replace(/s$/, "").toLowerCase()}`}
              </div>
              <button onClick={() => setForm(null)} data-testid="master-form-close" className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Code *">
                <Input value={form.code} onChange={set("code")} placeholder="e.g. WTI" disabled={!!form.id} data-testid="master-code" />
              </Field>
              <Field label="Name *">
                <Input value={form.name} onChange={set("name")} placeholder="Full name" data-testid="master-name" />
              </Field>
              {cfg.attrs.map((a) => (
                <Field key={a.k} label={a.label}>
                  {a.type === "select" ? (
                    <Select value={form.attrs[a.k] || ""} onChange={setAttr(a.k)} data-testid={`master-attr-${a.k}`}>
                      <option value="">Select…</option>
                      {a.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </Select>
                  ) : (
                    <Input type={a.type} value={form.attrs[a.k] ?? ""} onChange={setAttr(a.k)} data-testid={`master-attr-${a.k}`} />
                  )}
                </Field>
              ))}
            </div>
            <div className="mt-4">
              <Btn onClick={save} disabled={saving} data-testid="master-save-btn">
                {saving ? "Saving…" : form.id ? "Update" : "Create"}
              </Btn>
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <Th>Code</Th><Th>Name</Th>
              {cfg.attrs.map((a) => <Th key={a.k}>{a.label}</Th>)}
              <Th className="w-16" />
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`master-row-${m.code}`}>
                <td className="py-2 pr-3 font-mono text-xs font-semibold text-[#0B1E3F]">{m.code}</td>
                <td className="py-2 pr-3">{m.name}</td>
                {cfg.attrs.map((a) => (
                  <td key={a.k} className="py-2 pr-3 text-xs text-slate-600">
                    {a.type === "number" && m.attrs?.[a.k] !== undefined && m.attrs?.[a.k] !== ""
                      ? Number(m.attrs[a.k]).toLocaleString("en-US")
                      : (m.attrs?.[a.k] ?? "—")}
                  </td>
                ))}
                <td className="py-2">
                  <div className="flex gap-1.5">
                    <button onClick={() => setForm({ id: m.id, code: m.code, name: m.name, attrs: { ...m.attrs } })}
                      data-testid={`master-edit-${m.code}`} className="text-slate-400 hover:text-[#0B1E3F]" title="Edit"><PencilSimple size={15} /></button>
                    <button onClick={() => remove(m)} data-testid={`master-delete-${m.code}`}
                      className="text-slate-400 hover:text-red-600" title="Delete"><Trash size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3 + cfg.attrs.length} className="py-6 text-center text-slate-400 text-xs" data-testid="masters-empty">
                No {cfg.label.toLowerCase()} yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
